import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api-client';
import { enumLabel } from '../lib/labels';
import { formatMoney, sterlingBooksLine } from '../lib/money';
import { useFreshRows } from '../lib/select-choices';
import { useLedgerReload } from '../lib/use-ledger';
import { Button } from '../components/ui/Button';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { useAuth } from '../contexts/AuthContext';
import { RecordStepper } from '../components/records/RecordStepper';
import { PageHead, RecordLink, SectionCard } from '../components/ui/chrome';
import { Field, Select } from '../components/ui/Input';
import { useNotice } from '../components/ui/Notice';

export function BillPage() {
  const { id } = useParams();
  const notice = useNotice();
  const { can } = useAuth();
  const [assist, setAssist] = useState(false);
  const [bill, setBill] = useState<Record<string, unknown> | null>(null);
  const [orders, reloadOrders] = useFreshRows<{ id: string; poNumber?: string }>('/api/purchase-orders?direction=supplier');
  const reload = useCallback(() => {
    if (!id) return;
    void apiGet<Record<string, unknown>[]>('/api/bills').then((list) => setBill(list.find((row) => row.id === id) || null));
  }, [id]);
  useEffect(() => { reload(); }, [reload]);
  useLedgerReload('bills', reload);
  if (!bill) {
    return <PageHead stepper={id ? <RecordStepper listUrl="/api/bills" id={id} basePath="/bills" /> : null} crumbs={[{ label: 'Bills', to: '/bills' }, { label: 'Bill' }]} back={{ label: 'Back to bills', to: '/bills' }} title="Bill" />;
  }
  const status = String(bill.status || '');
  const books = sterlingBooksLine(bill.currency, bill.gbpAmountPence, bill.fxRate, bill.fxRateOn);
  return (
    <div className="space-y-4">
      <PageHead
        stepper={id ? <RecordStepper listUrl="/api/bills" id={id} basePath="/bills" /> : null}
        crumbs={[{ label: 'Bills', to: '/bills' }, { label: String(bill.number) }]}
        back={{ label: 'Back to bills', to: '/bills' }}
        title={String(bill.number)}
        subtitle={enumLabel(status)}
        actions={
        <div className="flex gap-2">
          {can('bills', 'ingest') ? <EditWithAiButton kind="bill" status={status} onClick={() => setAssist(true)} /> : null}
          {status === 'mismatch' || status === 'draft' || status === 'on_hold' ? <Button onClick={() => void notice.run('Approving the bill', async () => { setBill(await apiPost<Record<string, unknown>>(`/api/bills/${bill.id}/approve`, { override: false })); }, 'Bill approved')}>Approve</Button> : null}
          {status === 'mismatch' ? <Button variant="outline" onClick={() => void notice.run('Overriding the mismatch', async () => { setBill(await apiPost<Record<string, unknown>>(`/api/bills/${bill.id}/approve`, { override: true })); }, 'Mismatch overridden', { confirm: 'This will approve the bill even though it does not match the timesheet.' })}>Override mismatch</Button> : null}
          {status === 'approved' ? <Button onClick={() => void notice.run('Scheduling payment', async () => { setBill(await apiPost<Record<string, unknown>>(`/api/bills/${bill.id}/schedule-payment`)); }, 'Payment scheduled')}>Schedule payment</Button> : null}
        </div>
      } />
      {assist ? <RecordAssistDialog kind="bill" mode="edit" recordId={String(bill.id)} current={bill} title="Bill" onClose={() => setAssist(false)} onSaved={() => reload()} /> : null}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="text-sm">qty {String(bill.qty)} {String(bill.unit)}</div>
        <div className="text-3xl">{formatMoney(bill.amountPence, String(bill.currency || 'GBP'))}</div>
        {books ? <div className="mt-1 text-sm text-gray-500">{books}</div> : null}
        <p className="mt-2 text-sm">{String(bill.periodStart || '')} to {String(bill.periodEnd || '')}</p>
      </div>
      <SectionCard title="Purchase order" hint="The purchase order Maxiron raised for this bill.">
        {can('bills', 'ingest') ? (
          <Field label="Purchase order">
            <Select searchable choices={[{ value: '', label: 'None' }, ...orders.map((order) => ({ value: order.id, label: order.poNumber || order.id }))]} onOpen={reloadOrders} value={String(bill.poId || '')} onChange={(event) => {
              const next = event.target.value;
              void notice.run('Saving the purchase order', () => apiPut(`/api/bills/${bill.id}`, { ...bill, poId: next }), 'Purchase order saved').then(() => reload());
            }} />
          </Field>
        ) : bill.poNumber ? <p className="text-sm">{String(bill.poNumber)}</p> : null}
        {bill.poId ? <RecordLink to={`/supplier-purchase-orders/${bill.poId}`} backLabel={`Back to ${String(bill.number || 'bill')}`}>Open purchase order</RecordLink> : null}
      </SectionCard>
      <SectionCard title="Payments out" hint="Payments recorded against this bill.">
        <div className="flex flex-col gap-2">
          <RecordLink to={`/payments-out?billId=${encodeURIComponent(String(bill.id))}&billNumber=${encodeURIComponent(String(bill.number || ''))}`} backLabel={`Back to ${String(bill.number || 'bill')}`}>Open payments out</RecordLink>
          {((bill.paymentsOut as { id?: string; amountPence?: number; paidOn?: string }[]) || []).map((pay) => (
            <span key={String(pay.id)} className="text-sm">{formatMoney(Number(pay.amountPence || 0), String(bill.currency || 'GBP'))} · {String(pay.paidOn || '')}</span>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
